import { Trans } from '@lingui/react/macro';
import { useMemo, useState } from 'react';

import { type CampaignActivityDataType } from '@/api/campaign';
import { type TRAFFIC_TYPES } from '@/api/common';
import {
  InstantSearchDataTable,
  type InstantSearchDataTableColumnDef,
} from '@/components/InstantSearch';
import { usePageFilterContext } from '@/components/page';
import { Panel } from '@/components/panels';
import type SolAPIError from '@/error/SolAPIError';
import DataTableAdvancedFiltersDialog from '@/features/AdvancedFiltersModal/DataTableAdvancedFiltersDialog';
import { DataTableMetricSelector } from '@/features/DataTableMetricSelector';
import { VisitActivityLegend } from '@/features/VisitActivityLegend';

import CampaignActivityTableActionMenu from './CampaignActivityTableActionMenu';
import CampaignTableActionContent from './CampaignTableActionContent';

type Props = {
  columns: InstantSearchDataTableColumnDef<CampaignActivityDataType>[];
  selectedCampaigns: CampaignActivityDataType[];
  isMaxCompareLimit: boolean;
  data?: CampaignActivityDataType[];
  totalResults?: number;
  isLoading?: boolean;
  error: SolAPIError | null;
  onScoreCampaigns: (campaignIds: string[]) => void;
  onDisabledSeriesChange: (disabledSeriesType?: TRAFFIC_TYPES[]) => void;
  onSelectedCampaignsChange: (campaigns: CampaignActivityDataType[]) => void;
};

const CampaignActivityTable = ({
  columns,
  selectedCampaigns,
  isMaxCompareLimit,
  data,
  totalResults,
  isLoading,
  error,
  onScoreCampaigns,
  onDisabledSeriesChange,
  onSelectedCampaignsChange,
}: Props) => {
  const {
    pageFilters: { audience },
  } = usePageFilterContext();
  const [metricSelectorOpen, setMetricSelectorOpen] = useState(false);
  const [advancedFiltersOpen, setAdvancedFiltersOpen] = useState(false);
  const selectedCampaignIds = useMemo(
    () => selectedCampaigns.map((campaign) => campaign.ad.id),
    [selectedCampaigns],
  );
  const selectedCampaignById = useMemo(
    () => new Map(selectedCampaigns.map((campaign) => [campaign.ad.id, campaign])),
    [selectedCampaigns],
  );
  const visibleCampaignById = useMemo(
    () => new Map((data ?? []).map((campaign) => [campaign.ad.id, campaign])),
    [data],
  );

  const handleSelectRow = (campaign: CampaignActivityDataType) => {
    const isCampaignSelected = selectedCampaignIds.includes(campaign.ad.id);

    if (!isCampaignSelected && isMaxCompareLimit) {
      return;
    }

    if (isCampaignSelected) {
      onSelectedCampaignsChange(
        selectedCampaigns.filter((selectedCampaign) => selectedCampaign.ad.id !== campaign.ad.id),
      );
      return;
    }

    onSelectedCampaignsChange([...selectedCampaigns, campaign]);
  };

  const handleRemoveSelectedCampaign = (campaign: CampaignActivityDataType) => {
    onSelectedCampaignsChange(
      selectedCampaigns.filter((selectedCampaign) => selectedCampaign.ad.id !== campaign.ad.id),
    );
  };

  const handleSelectedCampaignIdsChange = (selectedRowIds: string[]) => {
    const nextSelectedCampaigns = selectedRowIds.flatMap((campaignId) => {
      const existingCampaign = selectedCampaignById.get(campaignId);
      if (existingCampaign != null) {
        return [existingCampaign];
      }

      const visibleCampaign = visibleCampaignById.get(campaignId);
      return visibleCampaign != null ? [visibleCampaign] : [];
    });

    onSelectedCampaignsChange(nextSelectedCampaigns);
  };

  return (
    <Panel
      title={<Trans>Campaign Reporting</Trans>}
      actions={
        <CampaignActivityTableActionMenu
          onOpenMetricSelector={() => setMetricSelectorOpen(true)}
          onOpenAdvancedFilters={() => setAdvancedFiltersOpen(true)}
        />
      }
      verifyC99Tag
      isFullHeight
    >
      <VisitActivityLegend audience={audience} onDisabledSeriesChange={onDisabledSeriesChange} />
      <InstantSearchDataTable<CampaignActivityDataType>
        columns={columns}
        data={data}
        totalResults={totalResults}
        loading={isLoading}
        error={error}
        emptyMessage={<Trans>No campaigns found.</Trans>}
        getRowId={(record) => record.ad.id}
        initialColumnPinning={{
          left: ['campaignId'],
        }}
        rowSelection={{
          selectedRowIds: selectedCampaignIds,
          onSelectedRowIdsChange: handleSelectedCampaignIdsChange,
          getRowCanSelect: (row) =>
            selectedCampaignIds.includes(row.original.ad.id) || !isMaxCompareLimit,
          enableSelectAll: true,
          columnWidth: 40,
        }}
        actionContent={
          <CampaignTableActionContent
            selectedCampaigns={selectedCampaigns}
            onRemoveSelectedCampaign={handleRemoveSelectedCampaign}
            onScoreCampaigns={onScoreCampaigns}
          />
        }
        onRow={(record) => ({
          onClick: () => handleSelectRow(record),
        })}
      />
      <DataTableMetricSelector
        open={metricSelectorOpen}
        onClose={() => setMetricSelectorOpen(false)}
      />
      <DataTableAdvancedFiltersDialog
        open={advancedFiltersOpen}
        onClose={() => setAdvancedFiltersOpen(false)}
      />
    </Panel>
  );
};

export default CampaignActivityTable;
